/**
 * Toucan Music: what is known about a record and its artist, as opposed to
 * your copy of it. Fetched when an album's page opens, since it is large, goes
 * stale and most albums are never opened. Kept in the per-device cache, not
 * the synced store.
 *
 * MusicBrainz links to Wikidata rather than Wikipedia, so the prose takes two
 * more calls, one to Wikidata for the article title and one to Wikipedia for
 * its opening.
 */

import { cached, putCached, stated } from "toucan";
import { read } from "./musicbrainz.ts";
import { rankedGenres, artistLine } from "./model.ts";
import type { Album, ArtistFacts, Prose } from "./model.ts";

/** How long fetched detail is kept. A lead section rarely changes much. */
const PROSE_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

/** What was found out about the record, present only when something was. */
export interface RecordFacts {
  /** The release group this was read from, for a link back to it. */
  releaseGroup: string;
  prose?: Prose;
  /** Genres MusicBrainz's editors have voted for, most-voted first. */
  genres?: string[];
}

/** Everything the album page shows that did not come from your own library. */
export interface AlbumDetail {
  record?: RecordFacts;
  /** About whoever made it. */
  artist?: ArtistFacts;
}

interface Relation {
  type?: string;
  url?: { resource?: string };
}

interface GroupLookup {
  genres?: { name: string; count: number }[];
  relations?: Relation[];
}

interface ArtistLookup {
  name?: string;
  type?: string | null;
  area?: { name?: string | null } | null;
  "life-span"?: { begin?: string | null; end?: string | null } | null;
  relations?: Relation[];
}

interface WikidataLookup {
  entities?: Record<string, { sitelinks?: { enwiki?: { title?: string } } }>;
}

interface SummaryLookup {
  extract?: string;
  content_urls?: { desktop?: { page?: string } };
}

/**
 * Everything the page can show about a record, or `null` when a request
 * throws. An answer, even an empty one, is cached for `PROSE_MAX_AGE`; a
 * failure isn't cached, so the next visit tries again.
 */
export async function albumDetail(
  album: Album,
  signal: AbortSignal,
): Promise<AlbumDetail | null> {
  const key = `music:detail:${album.ids.mbReleaseGroup ?? ""}:${album.ids.mbArtist ?? ""}`;
  const kept = await cached<AlbumDetail>(key, PROSE_MAX_AGE);
  if (kept !== undefined) {
    return kept;
  }
  const detail: AlbumDetail = {};
  try {
    await addRecord(detail, album.ids.mbReleaseGroup, signal);
    await addArtist(detail, album.ids.mbArtist, signal);
  } catch {
    return null;
  }
  await putCached(key, detail);
  return detail;
}

/** Whether anything at all was found out. */
export function isEmpty(detail: AlbumDetail): boolean {
  return detail.record === undefined && detail.artist === undefined;
}

/** The record's genres and article, when it has a release group. */
async function addRecord(
  detail: AlbumDetail,
  releaseGroup: string | undefined,
  signal: AbortSignal,
): Promise<void> {
  if (releaseGroup === undefined) {
    return;
  }
  const group = await read<GroupLookup>(
    `/release-group/${releaseGroup}?inc=genres+url-rels`,
    signal,
  );
  if (group === null) {
    return;
  }
  const genres = rankedGenres(group.genres ?? []);
  const prose = await proseFor(group.relations, signal);
  // No card just for a link to an empty entry.
  if (genres.length === 0 && prose === undefined) {
    return;
  }
  const facts: RecordFacts = { releaseGroup };
  if (genres.length > 0) {
    facts.genres = genres;
  }
  if (prose !== undefined) {
    facts.prose = prose;
  }
  detail.record = facts;
}

/** The facts MusicBrainz holds on the artist, and their article. */
async function addArtist(
  detail: AlbumDetail,
  artistId: string | undefined,
  signal: AbortSignal,
): Promise<void> {
  if (artistId === undefined) {
    return;
  }
  const artist = await read<ArtistLookup>(
    `/artist/${artistId}?inc=url-rels`,
    signal,
  );
  if (artist === null) {
    return;
  }
  const name = stated(artist.name);
  if (name === undefined) {
    return;
  }
  const facts: ArtistFacts = {
    id: artistId,
    name,
    line: artistLine({
      type: artist.type,
      area: artist.area?.name,
      begin: artist["life-span"]?.begin,
      end: artist["life-span"]?.end,
    }),
  };
  const prose = await proseFor(artist.relations, signal);
  if (prose !== undefined) {
    facts.prose = prose;
  }
  detail.artist = facts;
}

/**
 * The Wikipedia opening for whatever a MusicBrainz entity's Wikidata relation
 * points at, where it has one and Wikidata has an English article for it.
 */
async function proseFor(
  relations: Relation[] | undefined,
  signal: AbortSignal,
): Promise<Prose | undefined> {
  const entity = wikidataId(relations ?? []);
  if (entity === undefined) {
    return undefined;
  }
  // Without `origin=*`, Wikidata doesn't send a CORS header at all.
  const wikidata = await readJson<WikidataLookup>(
    `https://www.wikidata.org/w/api.php?action=wbgetentities&ids=${entity}&props=sitelinks&sitefilter=enwiki&format=json&origin=*`,
    signal,
  );
  const title = wikidata?.entities?.[entity]?.sitelinks?.enwiki?.title;
  if (title === undefined) {
    return undefined;
  }
  const summary = await readJson<SummaryLookup>(
    `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`,
    signal,
  );
  const extract = summary?.extract;
  const url = summary?.content_urls?.desktop?.page;
  if (extract === undefined || url === undefined) {
    return undefined;
  }
  return { extract, url, title };
}

/** The Q-number a MusicBrainz entity's Wikidata relation names, if any. */
function wikidataId(relations: Relation[]): string | undefined {
  const resource = relations.find((relation) => relation.type === "wikidata")
    ?.url?.resource;
  return resource?.split("/").pop();
}

/**
 * One read from Wikidata or Wikipedia, or null where it has no such page. Any
 * other failure throws, so an outage isn't cached as "nothing written", and
 * so does an aborted `signal`.
 */
async function readJson<T>(
  url: string,
  signal: AbortSignal,
): Promise<T | null> {
  signal.throwIfAborted();
  const response = await fetch(url);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`${url} answered ${String(response.status)}`);
  }
  return (await response.json()) as T;
}
